/**
 * Redemption review console — triage queue (build seq #4, Pass 4c-1).
 *
 * Lists pending + held redemptions (band-ordered high→med→low, oldest-first) for
 * the care rep. Each row carries its risk band + one-line reason + member context
 * inline (no drill-in needed for the common case). Row actions come STRAIGHT from
 * the server's band-derived `actions` array (§3.2 enforced server-side — the UI
 * never re-derives the rule); status refines what's actionable (a held row can't
 * be re-held). Low rows are batch-approvable (cap 50, §8.2). High rows have no
 * row-level approve — they route through the drill-in (the exception path, §3.3).
 *
 * Mirrors the AdminBuyers / BuyerQualityPage conventions (dark theme, hand-rolled
 * table, offset pagination, inline reason-input, manual toast).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import {
  fetchRedemptionQueue,
  fetchRedemptionQueueCounts,
  approveRedemption,
  holdRedemption,
  rejectRedemption,
  batchApproveRedemptions,
  type RiskBandValue,
  type Trajectory,
} from '../../lib/adminApi';

const PAGE_SIZE = 25;
const BATCH_CAP = 50; // §8.2 blast-radius rail (server also enforces)

function bandPillClass(band: RiskBandValue): string {
  if (band === 'high') return 'bg-red-500/15 text-red-300';
  if (band === 'medium') return 'bg-amber-500/15 text-amber-300';
  return 'bg-emerald-500/15 text-emerald-300';
}

// A risk band is a review-priority signal (velocity / account age / value), NOT a
// fraud verdict — a brand-new fast cashout reads High at fraud_score 0. The reason
// is shown next to the pill; this tooltip keeps the pill itself from reading as "fraud".
const RISK_BAND_TOOLTIP = 'Review priority from redemption velocity, account age & value — not a fraud score. See the reason.';

function RiskPill({ band }: { band: RiskBandValue }) {
  return <span title={RISK_BAND_TOOLTIP} className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase cursor-help ${bandPillClass(band)}`}>{band}</span>;
}

function TrajectoryArrow({ t }: { t: Trajectory }) {
  const glyph = t === 'up' ? '↑' : t === 'down' ? '↓' : '→';
  const color = t === 'up' ? 'text-emerald-400' : t === 'down' ? 'text-red-400' : 'text-slate-500';
  return <span className={`${color} font-bold`} title={`trust ${t}`}>{glyph}</span>;
}

export default function AdminRedemptionQueue() {
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Inline reason editor: which row + which action is awaiting a reason.
  const [reasonFor, setReasonFor] = useState<{ id: string; kind: 'hold' | 'reject' } | null>(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'redemptions', 'queue', offset],
    queryFn: () => fetchRedemptionQueue({ limit: PAGE_SIZE, offset }),
  });
  const { data: counts } = useQuery({
    queryKey: ['admin', 'redemptions', 'counts'],
    queryFn: fetchRedemptionQueueCounts,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'redemptions'] });
  }
  function clearReason() { setReasonFor(null); setReason(''); }

  const approveM = useMutation({
    mutationFn: (id: string) => approveRedemption(id),
    onSuccess: (r) => { showToast(`Approved — ${r.status}`); invalidate(); },
    onError: (e: Error) => showToast(e.message),
  });
  const holdM = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => holdRedemption(id, reason),
    onSuccess: () => { showToast('Held'); clearReason(); invalidate(); },
    onError: (e: Error) => showToast(e.message),
  });
  const rejectM = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rejectRedemption(id, reason),
    onSuccess: () => { showToast('Rejected — points refunded'); clearReason(); invalidate(); },
    onError: (e: Error) => showToast(e.message),
  });
  const batchM = useMutation({
    mutationFn: (ids: string[]) => batchApproveRedemptions(ids),
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      showToast(failed.length ? `Approved ${r.approved}/${r.requested} — ${failed.length} skipped` : `Approved all ${r.approved}`);
      setSelected(new Set());
      invalidate();
    },
    onError: (e: Error) => showToast(e.message),
  });

  const anyPending = approveM.isPending || holdM.isPending || rejectM.isPending || batchM.isPending;

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < BATCH_CAP) next.add(id); // cap selection at 50
      return next;
    });
  }

  const rows = data?.rows ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold font-display text-white">Redemptions</h1>
        {counts && (
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full font-medium bg-red-500/15 text-red-300">High {counts.high}</span>
            <span className="px-2 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-300">Medium {counts.medium}</span>
            <span className="px-2 py-0.5 rounded-full font-medium bg-emerald-500/15 text-emerald-300">Low {counts.low}</span>
            {counts.uncomputed > 0 && (
              <span className="px-2 py-0.5 rounded-full font-medium bg-fuchsia-500/20 text-fuchsia-300" title="Rows with no cached risk band — check these (they sort last but may be High)">
                Uncomputed {counts.uncomputed}
              </span>
            )}
            <span className="text-slate-500">· {counts.total} open</span>
          </div>
        )}
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between bg-slate-800 rounded-xl px-4 py-3">
          <span className="text-sm text-slate-300">
            {selected.size} selected{selected.size >= BATCH_CAP ? ` (max ${BATCH_CAP})` : ''}
          </span>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="text-xs px-3 py-1.5 text-slate-400 hover:text-white">Clear</button>
            <button
              onClick={() => batchM.mutate([...selected])}
              disabled={batchM.isPending}
              className="bg-primary text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {batchM.isPending ? 'Approving…' : `Approve selected (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      <div className="bg-slate-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-slate-400 text-sm">Loading…</div>
        ) : !rows.length ? (
          <div className="p-6 text-slate-400 text-sm">Queue is clear — no pending or held redemptions.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 w-8"></th>
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-left px-4 py-3">Redemption</th>
                <th className="text-left px-4 py-3">Risk</th>
                <th className="text-left px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const r = row.redemption;
                // §3.2 actions are fully server-derived (band + status) — render straight.
                const acts = row.actions;
                const editing = reasonFor?.id === r.id;
                return (
                  <tr key={r.id} className="border-t border-slate-700 hover:bg-slate-700/40 align-top">
                    <td className="px-4 py-3">
                      {row.batch_eligible && r.status === 'pending' && (
                        <input
                          type="checkbox"
                          aria-label={`select ${r.id}`}
                          checked={selected.has(r.id)}
                          disabled={!selected.has(r.id) && selected.size >= BATCH_CAP}
                          onChange={() => toggleSelect(r.id)}
                        />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{row.member.display_name ?? '—'}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-1">
                        {row.member.trust_tier_name} <TrajectoryArrow t={row.member.trust_trajectory} />
                        <span className="text-slate-600">·</span> {row.member.account_age_days}d old
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">${r.usd_value.toFixed(2)}</div>
                      <div className="text-xs text-slate-400">{r.brand ?? r.sku ?? '—'}</div>
                      {r.status === 'held' && <span className="text-[10px] uppercase text-amber-400">held</span>}
                    </td>
                    <td className="px-4 py-3">
                      <RiskPill band={row.risk.band} />
                      <div className="text-xs text-slate-400 mt-1 max-w-[16rem]">{row.risk.reason}</div>
                      {row.risk.ai && row.risk.ai.recommendation !== 'none' && (
                        <div className="text-xs text-sky-300 mt-1 max-w-[16rem] flex items-start gap-1">
                          <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
                          <span><span className="font-semibold uppercase">AI: {row.risk.ai.recommendation}</span> — {row.risk.ai.reasoning}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editing ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            autoFocus
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={reasonFor.kind === 'hold' ? 'Reason (required)' : 'Reason (optional)'}
                            className="w-40 px-2 py-1 rounded text-xs bg-slate-700 text-white border border-slate-600"
                          />
                          <button
                            onClick={() =>
                              reasonFor.kind === 'hold'
                                ? holdM.mutate({ id: r.id, reason })
                                : rejectM.mutate({ id: r.id, reason: reason.trim() || undefined })
                            }
                            disabled={anyPending || (reasonFor.kind === 'hold' && !reason.trim())}
                            className="px-2 py-1 rounded text-xs font-medium bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
                          >
                            Confirm
                          </button>
                          <button onClick={clearReason} className="px-2 py-1 rounded text-xs font-medium text-slate-400 hover:text-white">Cancel</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          {acts.includes('approve') && (
                            <button
                              onClick={() => approveM.mutate(r.id)}
                              disabled={anyPending}
                              className="px-2 py-1 rounded text-xs font-medium bg-emerald-900/50 text-emerald-300 hover:bg-emerald-900/70 disabled:opacity-40"
                            >
                              Approve
                            </button>
                          )}
                          {acts.includes('hold') && (
                            <button
                              onClick={() => { setReasonFor({ id: r.id, kind: 'hold' }); setReason(''); }}
                              className="px-2 py-1 rounded text-xs font-medium bg-amber-900/50 text-amber-300 hover:bg-amber-900/70"
                            >
                              Hold
                            </button>
                          )}
                          {acts.includes('reject') && (
                            <button
                              onClick={() => { setReasonFor({ id: r.id, kind: 'reject' }); setReason(''); }}
                              className="px-2 py-1 rounded text-xs font-medium bg-red-900/50 text-red-300 hover:bg-red-900/70"
                            >
                              Reject
                            </button>
                          )}
                          <Link
                            to={`/admin/redemptions/${r.id}`}
                            className="px-2 py-1 rounded text-xs font-medium text-slate-300 hover:text-white inline-flex items-center gap-1"
                          >
                            Detail <ArrowUpRight className="w-3 h-3" />
                          </Link>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            {offset + 1}–{offset + rows.length} of {data.total}
          </p>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={data.nextOffset == null}
              onClick={() => data.nextOffset != null && setOffset(data.nextOffset)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 text-slate-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-slate-900 border border-slate-700 text-white text-sm font-medium px-5 py-3 rounded-xl shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
